import { validateIconProps, resolveIcon, warn, validateIconDefaults } from './chunk-CQT6I7B4.js';
import { createContext, forwardRef, useId, useContext } from 'react';
import { ErrorBoundary } from '@norkron/web-error-boundary';
import { useFocusRestore } from '@norkron/web-a11y';
import { RichTooltip } from '@norkron/web-tooltip';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/version.ts
var LIB_VERSION = "0.4.0";

// src/weights.ts
var ICON_WEIGHTS = ["regular", "filled"];
var FILLED_ICON_NAMES = /* @__PURE__ */ new Set([
  "Heart",
  "Star",
  "Bell",
  "BellOff",
  "Bookmark",
  "Check",
  "CheckCircle",
  "CheckSquare",
  "Circle",
  "Square",
  "Triangle",
  "Plus",
  "PlusCircle",
  "PlusSquare",
  "Minus",
  "MinusCircle",
  "MinusSquare",
  "X",
  "XCircle",
  "XSquare",
  "AlertCircle",
  "AlertTriangle",
  "Info",
  "HelpCircle",
  "Eye",
  "EyeOff",
  "User",
  "Users",
  "Home",
  "Mail",
  "MessageCircle",
  "MessageSquare",
  "Phone",
  "PhoneCall",
  "Search",
  "Settings",
  "Trash",
  "Trash2",
  "Folder",
  "File",
  "FileText",
  "Image",
  "Camera",
  "Video",
  "Lock",
  "Unlock",
  "Calendar",
  "Clock",
  "Tag",
  "ShoppingCart"
]);
function hasFilledVariant(name) {
  return FILLED_ICON_NAMES.has(name);
}
var ICON_BUILTIN_DEFAULTS = Object.freeze({
  strokeWidth: 2,
  size: 24,
  weight: "regular",
  color: void 0
});
var IconDefaultsContext = createContext(void 0);
var moduleDefaults = {};
function setIconDefaults(next) {
  if (!validateIconDefaults(next, "setIconDefaults")) return { ...moduleDefaults };
  moduleDefaults = { ...moduleDefaults, ...next };
  return { ...moduleDefaults };
}
function getIconDefaults() {
  return { ...moduleDefaults };
}
function resetIconDefaults() {
  moduleDefaults = {};
}
function IconDefaultsProvider(props) {
  const { children, ...defaults } = props;
  const inherited = useContext(IconDefaultsContext);
  validateIconDefaults(defaults, "IconDefaultsProvider");
  const own = {};
  for (const [k, v] of Object.entries(defaults)) {
    if (v !== void 0) own[k] = v;
  }
  const merged = { ...inherited, ...own };
  return /* @__PURE__ */ jsx(IconDefaultsContext.Provider, { value: merged, children });
}
function useIconDefaults() {
  const fromProvider = useContext(IconDefaultsContext);
  return { ...moduleDefaults, ...fromProvider };
}
function resolveIconDefaults(perCall, ambient) {
  const pick = (key) => perCall[key] !== void 0 ? perCall[key] : ambient[key] !== void 0 ? ambient[key] : ICON_BUILTIN_DEFAULTS[key];
  return {
    strokeWidth: pick("strokeWidth"),
    size: pick("size"),
    weight: pick("weight"),
    color: pick("color")
  };
}
var _filledFallbackWarned = /* @__PURE__ */ new Set();
var _missingWarned = /* @__PURE__ */ new Set();
var MISSING_ICON_SHAPES = [
  { type: "rect", attrs: { x: "3", y: "3", width: "18", height: "18", rx: "3" } },
  { type: "circle", attrs: { cx: "12", cy: "12", r: "1.25" } }
];
var Icon = forwardRef(function Icon2(props, ref) {
  validateIconProps(props);
  const {
    name,
    size: sizeProp,
    color: colorProp,
    strokeWidth: strokeWidthProp,
    weight: weightProp,
    className,
    title,
    tooltip,
    style,
    // Pulled OUT of `rest` deliberately. `rest` is spread LAST onto the <svg>,
    // so leaving it in meant the raw prop silently overrode the computed
    // `aria-hidden` below — two sources of truth for one attribute, and the
    // attribute emitted twice.
    "aria-hidden": ariaHiddenProp,
    ...rest
  } = props;
  const ambient = useIconDefaults();
  const { size, color, strokeWidth, weight } = resolveIconDefaults(
    {
      size: sizeProp,
      color: colorProp,
      strokeWidth: strokeWidthProp,
      weight: weightProp
    },
    ambient
  );
  const ariaLabel = props["aria-label"];
  const ariaHidden = ariaHiddenProp === void 0 ? ariaLabel === void 0 : ariaHiddenProp !== false && ariaHiddenProp !== "false";
  const titleId = useId();
  useFocusRestore(false);
  const resolved = resolveIcon(name);
  const missing = resolved === null;
  if (missing) {
    const key = String(name);
    if (!_missingWarned.has(key)) {
      _missingWarned.add(key);
      warn({
        fn: "Icon",
        code: "WEB_ICONS_UNKNOWN_ICON_NAME",
        message: `unknown icon name "${key}" \u2014 rendering the placeholder glyph. Curate it in tools/icon-slugs.mjs + \`bun tools/sync-icons.mjs\` rather than calling registerCustomIcon in the consumer.`
      });
    }
  }
  const shapes = resolved ?? MISSING_ICON_SHAPES;
  let effectiveWeight = weight;
  if (weight === "filled" && !hasFilledVariant(String(name))) {
    const key = String(name);
    if (!_filledFallbackWarned.has(key)) {
      _filledFallbackWarned.add(key);
      warn({
        fn: "Icon",
        code: "WEB_ICONS_INVALID_INPUT",
        message: `no filled variant for "${key}" \u2014 falling back to regular`
      });
    }
    effectiveWeight = "regular";
  }
  const mergedClassName = [
    "icon",
    `icon--${String(name)}`,
    `icon--weight-${effectiveWeight}`,
    missing ? "icon--missing" : null,
    className
  ].filter(Boolean).join(" ");
  const paintColor = color ?? "currentColor";
  const isFilled = effectiveWeight === "filled";
  const strokeColor = isFilled ? "none" : paintColor;
  const fillColor = isFilled ? paintColor : "none";
  const mergedStyle = style;
  const isMeaningful = Boolean(ariaLabel) || Boolean(title);
  const resolvedRole = isMeaningful ? "img" : void 0;
  const svg = /* @__PURE__ */ jsxs(
    "svg",
    {
      ref,
      xmlns: "http://www.w3.org/2000/svg",
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: isFilled ? 0 : strokeWidth,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: mergedClassName,
      style: mergedStyle,
      role: resolvedRole,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHidden ? true : void 0,
      "aria-labelledby": title ? titleId : void 0,
      "data-icon-name": String(name),
      "data-icon-weight": effectiveWeight,
      "data-icon-missing": missing ? "true" : void 0,
      "data-norkron-lib": `@norkron/web-icons@${LIB_VERSION}`,
      "data-allow-single-theme": color === void 0 ? "true" : void 0,
      "data-allow-flush-siblings": "true",
      ...rest,
      children: [
        title ? /* @__PURE__ */ jsx("title", { id: titleId, children: title }) : null,
        shapes.map((s, i) => renderShape(s, i))
      ]
    }
  );
  const wrapped = /* @__PURE__ */ jsx(ErrorBoundary, { fallback: svg, children: tooltip ? /* @__PURE__ */ jsx(RichTooltip, { trigger: svg, content: tooltip }) : svg });
  return wrapped;
});
function renderShape(shape, key) {
  const a = shape.attrs;
  switch (shape.type) {
    case "path":
      return /* @__PURE__ */ jsx("path", { d: a.d }, key);
    case "circle":
      return /* @__PURE__ */ jsx(
        "circle",
        {
          cx: a.cx,
          cy: a.cy,
          r: a.r
        },
        key
      );
    case "rect":
      return /* @__PURE__ */ jsx(
        "rect",
        {
          x: a.x,
          y: a.y,
          width: a.width,
          height: a.height,
          rx: a.rx,
          ry: a.ry
        },
        key
      );
    case "line":
      return /* @__PURE__ */ jsx("line", { x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2 }, key);
    case "polyline":
      return /* @__PURE__ */ jsx("polyline", { points: a.points }, key);
    case "polygon":
      return /* @__PURE__ */ jsx("polygon", { points: a.points }, key);
    case "ellipse":
      return /* @__PURE__ */ jsx(
        "ellipse",
        {
          cx: a.cx,
          cy: a.cy,
          rx: a.rx,
          ry: a.ry
        },
        key
      );
    default:
      return null;
  }
}

export { FILLED_ICON_NAMES, ICON_BUILTIN_DEFAULTS, ICON_WEIGHTS, Icon, IconDefaultsProvider, LIB_VERSION, getIconDefaults, hasFilledVariant, resetIconDefaults, resolveIconDefaults, setIconDefaults, useIconDefaults };
//# sourceMappingURL=chunk-SAYUQY3J.js.map
//# sourceMappingURL=chunk-SAYUQY3J.js.map